import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import SVG from "@/assets/svg";
import { ROSTER_LOCATION_MISMATCH_FLAG } from "@/config/releaseFlags";
import { Staff } from "@/interfaces/IAuth";
import { TASK } from "@/interfaces/IRoster";
import { Visit } from "@/interfaces/IServiceUser";
import {
  deleteManyVisitAsync,
  deleteVisitAsync,
  getVisitsByOriginAsync,
  updateVisitAsync,
} from "@/redux/actions/roster";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setSelectedVisit } from "@/redux/reducers/roster";
import { getLogsByVisitId } from "@/services/roster";
import {
  $term,
  getCheckoutReasonType,
  getId,
  mergeDateAndTime,
} from "@/utils/helpers";
import logger from "@/runtime-utils/observability/logger";
import { useFeatureFlags } from "@/utils/providers/feature-flags-provider";
import { errorToast, successToast } from "@/component/toasts";
import CancelVisit from "../confirmVisitCancel";
import DialogueBox from "../dialogueBox";
import DeleteVisit from "../dialogueBox/confirmVisitDelete";
import LocationMismatch from "../dialogueBox/locationMismatch";
import ManualCheckInOutInfo from "../dialogueBox/manualCheckInOutInfo";
import VisitDetail from "../dialogueBox/visit-info";
import DefaultLoader from "../loader";
import { DrawerStyled } from "./drawerStyled";
import CheckoutReason from "../dialogueBox/checkoutReason";
import { getTenantSettings } from "@/services/settings";
import { displayText } from "@/utils/displayValue";
import { GridArrowDownwardIcon, GridArrowUpwardIcon } from "@mui/x-data-grid";

dayjs.extend(duration);

export type TCheckInCheckOut = {
  _id: string;
  visitId: string;
  carer: Staff;
  checkIn: Date;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  checkOut: Date;
  auto?: boolean;
  id: string;
  manuallyDoneBy?: Staff;
  checkInNote?: string;
  checkOutNote?: string;
  forcedCheckIn?: boolean;
  forcedCheckOut?: boolean;
  checkInInfo?: {
    accuracy?: number;
    clientLocation?: {
      lat: number;
      lng: number;
    };
    clientAddress?: string;
    coord?: {
      lat?: number;
      lng?: number;
    };
  };
  checkOutInfo?: {
    accuracy?: number;
    clientLocation?: {
      lat: number;
      lng: number;
    };
    clientAddress?: string;
    coord?: {
      lat?: number;
      lng?: number;
    };
  };
  lateOrEarlyCheckoutReason?: string;
  lateOrEarlyCheckoutReasonNote?: string;
};

function DetailTab() {
  const dispatch = useAppDispatch();
  const { isEnabled } = useFeatureFlags();
  const locationMismatchEnabled = isEnabled(
    ROSTER_LOCATION_MISMATCH_FLAG,
    false
  );

  const [open, setOpen] = React.useState(false);
  const [totalLogs, setTotalLogs] = useState<TCheckInCheckOut[]>([]);
  const [checkIns, setCheckIns] = React.useState<TCheckInCheckOut[]>([]);
  const [checkOuts, setCheckOuts] = React.useState<TCheckInCheckOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openManualType, setOpenManualType] = useState<TASK | null>(null);
  const [isLocationMismatchOpen, setIsLocationMismatchOpen] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [selectedLog, setSelectedLog] = useState<
    | (TCheckInCheckOut & {
        type: "checkIn" | "checkOut";
      })
    | null
  >(null);
  const [setting, setSetting] = useState<any>();
  const [isCheckoutReasonDialog, setIsCheckoutReasonDialog] = useState(false);

  const { selectedVisit } = useAppSelector((state) => state.roster);
  const visit = selectedVisit?.extendedProps as Visit;

  const { data: terms } = useAppSelector((state) => state.terms.terms);

  const extendedProps = selectedVisit?.extendedProps as Visit;

  const isUnAllocated = extendedProps?.carerAssigned?.length === 0;

  const {
    serviceUser,
    carerAssigned = [],
    _id,
    carerReq,
    anotherCarers = [],
  } = extendedProps || {};

  const fetchSettings = async () => {
    try {
      const response = await getTenantSettings();
      if (response?.success && response?.data) {
        setSetting({
          overrunException: response.data?.overrunException,
          underrunException: response.data?.underrunException,
        });
      } else {
        errorToast(response.message);
      }
    } catch (error: any) {
      const message =
        error.response?.data?.message || error.message || "An error occurred";
      errorToast(message);
    }
  };

  const fetchCheckInCheckOut = async () => {
    try {
      if (!_id || isUnAllocated) return;
      setLoading(true);
      const response = await getLogsByVisitId(_id);

      if (response.success) {
        logger.debug("logs by visit", response.data);
        setTotalLogs(response.data);

        const { data = [] } = response;

        const checkIns: TCheckInCheckOut[] = [];
        const checkOuts: TCheckInCheckOut[] = [];

        data?.forEach((log: TCheckInCheckOut) => {
          if (log.checkIn) {
            checkIns.push(log);
          }
          if (log.checkOut) {
            checkOuts.push(log);
          }
        });

        setCheckIns(checkIns);
        setCheckOuts(checkOuts);
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLog = (
    log: TCheckInCheckOut,
    type: "checkIn" | "checkOut"
  ) => {
    setSelectedLog({ ...log, type });
  };

  const handleLocationMismatchClose = () => {
    setIsLocationMismatchOpen(false);
    setSelectedLog(null);
  };

  const handleCheckoutReasonClose = () => {
    setIsCheckoutReasonDialog(false);
    setSelectedLog(null);
  };

  const handleCancelOpen = () => setCancelConfirm(true);
  const handleCancelClose = () => setCancelConfirm(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDeleteClose = () => setDeleteConfirm(false);

  const handleVisitDelete = async (deleteAll: boolean) => {
    try {
      setDeleteLoading(true);
      const occurrenceDate = dayjs(selectedVisit?.start);
      const seriesId = extendedProps.origin?._id || extendedProps._id;

      if (deleteAll) {
        const prevDate = occurrenceDate.subtract(1, "day");
        const timeSource =
          extendedProps.origin?.startTime ||
          extendedProps.origin?.endTime ||
          extendedProps.startTime ||
          extendedProps.endTime;
        const newEndDate = mergeDateAndTime(
          prevDate,
          timeSource || prevDate
        ).toISOString();

        const res: any = await dispatch(
          updateVisitAsync({
            id: seriesId,
            data: { endDate: newEndDate },
          })
        );
        if (!res.payload || res.payload.success === false) {
          // Assuming updateVisitAsync returns a payload with success property if handled by parseResponse
          // or we can check error property.
        }

        const response: any = await dispatch(
          getVisitsByOriginAsync({ id: seriesId })
        );

        if (response.payload && Array.isArray(response.payload)) {
          const overridesToDelete = response.payload
            .filter(
              (v: any) => !dayjs(v.startDate).isBefore(occurrenceDate, "day")
            )
            .map((v: any) => v._id);

          if (overridesToDelete.length > 0) {
            await dispatch(deleteManyVisitAsync({ ids: overridesToDelete }));
          }
        }
      } else {
        const currentExdates = extendedProps.origin?._id
          ? extendedProps.origin.exdate || []
          : extendedProps.exdate || [];
        const dateString = occurrenceDate.format("YYYY-MM-DD");
        const exdate = [...new Set([...currentExdates, dateString])];

        if (extendedProps.origin?._id) {
          await Promise.all([
            dispatch(deleteVisitAsync(extendedProps._id)),
            dispatch(
              updateVisitAsync({
                id: extendedProps.origin._id,
                data: { exdate },
              })
            ),
          ]);
        } else {
          await dispatch(
            updateVisitAsync({
              id: extendedProps._id,
              data: { exdate },
            })
          );
        }
      }
      successToast("Visit deleted successfully");
      dispatch(setSelectedVisit(null));
    } catch (error) {
      logger.error("Error deleting visit", error);
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(false);
      handleDeleteClose();
    }
  };

  useEffect(() => {
    fetchCheckInCheckOut();
    fetchSettings();
  }, [_id]);

  const getTotalTime = (checkIn: Date, checkOut: Date) => {
    const diffInSeconds = checkOut
      ? dayjs(checkOut).diff(checkIn, "seconds", true)
      : dayjs().diff(checkIn, "seconds", true);

    const durationObj = dayjs.duration(diffInSeconds, "seconds");
    const hours = Math.floor(durationObj.asHours());
    const minutes = durationObj.minutes();
    const seconds = durationObj.seconds();

    return hours > 0
      ? `${hours} hour${hours > 1 ? "s" : ""}, ${minutes} minute${minutes !== 1 ? "s" : ""} and ${seconds} second${seconds !== 1 ? "s" : ""}`
      : minutes > 0
        ? `${minutes} minute${minutes !== 1 ? "s" : ""} and ${seconds} second${seconds !== 1 ? "s" : ""}`
        : `${seconds} second${seconds !== 1 ? "s" : ""}`;
  };

  const getPaidCarers = () => {
    if (
      !extendedProps?.cancelOptions?.payStaff ||
      extendedProps?.cancelOptions?.payStaff.length === 0
    ) {
      return "None";
    }
    const paidCarers = carerAssigned.filter((c) =>
      extendedProps.cancelOptions?.payStaff?.includes(c._id)
    );
    if (paidCarers.length === 0) {
      return "None";
    }
    return paidCarers.map((c, i) => (
      <Link
        key={c?._id}
        className="links"
        href={`/staff/staff-members/${c._id}`}
      >
        {displayText(c.name, c._id)}
        {i < paidCarers.length - 1 ? ", " : ""}
      </Link>
    ));
  };

  const getAllCarers = useMemo(() => {
    const another = anotherCarers.reduce(
      (acc: (Staff & { type?: string })[], curr) => {
        if (curr.carer) {
          acc.push({
            ...curr.carer,
            type: curr.type,
          });
        }
        return acc;
      },
      []
    );
    const allCarers: (Staff & { type?: string })[] = [
      ...carerAssigned,
      ...another,
    ];
    return allCarers;
  }, [anotherCarers, carerAssigned]);

  const getCheckoutUI = (
    type: "late" | "early" | string
  ): {
    icon: React.ReactElement;
    color: ChipProps["color"];
    label: string;
  } => {
    switch (type) {
      case "late":
        return {
          icon: <GridArrowUpwardIcon fontSize="small" />,
          color: "error",
          label: "Overrun Reason",
        };

      case "early":
        return {
          icon: <GridArrowDownwardIcon fontSize="small" />,
          color: "warning",
          label: "Underrun Reason",
        };

      default:
        return {
          icon: <InfoOutlined fontSize="small" />,
          color: "default",
          label: "Checkout Reason",
        };
    }
  };

  return (
    <DrawerStyled>
      <Box className="Detail">
        <Box className="planDetail">
          <Box className="planned">
            <Typography variant="h3" className="plan">
              Planned time
            </Typography>
            <span className="timer">
              {!dayjs(selectedVisit?.start)
                .utc()
                .isSame(dayjs(selectedVisit?.end).utc(), "day")
                ? `${dayjs(selectedVisit?.start).utc().format("ddd HH:mm")} - ${dayjs(selectedVisit?.end).utc().format("ddd HH:mm")}`
                : `${dayjs(selectedVisit?.start).utc().format("HH:mm")}-${dayjs(selectedVisit?.end).utc().format("HH:mm")}`}
            </span>
          </Box>
          <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Button
              className="cancel"
              style={{
                width: "100%",
              }}
              onClick={handleCancelOpen}
            >
              {!visit?.isCancelled ? "Cancel" : "Edit/Uncancel"}
            </Button>

            {/* <Button className="delete" onClick={handleDelete}>
              Delete{" "}
            </Button> */}

            <Button className="edit" onClick={handleOpen}>
              Edit{" "}
            </Button>
            <Box>
              <SVG.Clock width="20px" />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className="Detail">
        <Box className="planDetail">
          <Box className="planned">
            <Typography variant="h3" className="plan">
              {$term(terms?.serviceUser)} location
            </Typography>
            {serviceUser?.postCode ? (
              <span className="timer">
                {displayText(serviceUser?.postCode)}
              </span>
            ) : (
              <span className="timer">
                {serviceUser?.address?.buildingName &&
                  `, ${serviceUser?.address?.buildingName}`}
                {serviceUser?.address?.addressLine1 &&
                  `, ${serviceUser?.address?.addressLine1}`}
                {serviceUser?.address?.addressLine2 &&
                  `, ${serviceUser?.address?.addressLine2}`}
                {serviceUser?.address?.town &&
                  `, ${serviceUser?.address?.town}`}
                {serviceUser?.address?.country &&
                  `, ${serviceUser?.address?.country}`}
                {serviceUser?.address?.postCode &&
                  `, ${serviceUser?.address?.postCode}`}
              </span>
            )}
          </Box>
          <Box>
            <SVG.Map width="20px" />
          </Box>
        </Box>
      </Box>
      <Box className="Detail">
        <Box className="planDetail">
          <Box className="planned">
            <Typography variant="h3" className="plan">
              Number of {terms?.staff} required
            </Typography>
            <span className="timer">
              <span>{carerReq}</span>
            </span>
          </Box>
          <Box>
            <SVG.Users width="20px" />
          </Box>
        </Box>
      </Box>
      <Box className="Detail">
        <Box className="planDetail">
          <Box className="planned">
            <Typography variant="h3" className="plan">
              {terms?.staff} Assigned
            </Typography>
            <span className="timer">
              {!isUnAllocated &&
                carerAssigned?.map((resource, i) => (
                  <React.Fragment key={i}>
                    <Link
                      className="links"
                      href={`/staff/staff-members/${resource._id}`}
                    >
                      {displayText(resource.name, resource._id)}
                      {i < carerAssigned.length - 1 ? ", " : ""}
                    </Link>
                  </React.Fragment>
                ))}
              {isUnAllocated && <span>Unassigned</span>}
            </span>
          </Box>
          <Box>
            <SVG.Users width="20px" />
          </Box>
        </Box>
      </Box>

      {extendedProps?.isCancelled && (
        <Box className="Detail">
          <Box className="planDetail">
            <Box className="planned">
              <Typography variant="h3" className="plan">
                Cancellation Information
              </Typography>
              <div>
                <span className="timer">
                  Charge {$term(terms?.serviceUser)}:{" "}
                  {extendedProps?.cancelOptions?.chargeServiceUser
                    ? "Yes"
                    : "No"}
                </span>
                <span className="timer">
                  Pay {terms?.staff}s: {getPaidCarers()}
                </span>
                <span className="timer">
                  Reason:{" "}
                  {displayText(
                    extendedProps?.cancelOptions?.cancellationNote,
                    "Not provided"
                  )}
                </span>
              </div>
            </Box>
            <Box>
              <SVG.Users width="20px" />
            </Box>
          </Box>
        </Box>
      )}

      {!isUnAllocated && totalLogs.length > 0 && !loading ? (
        <>
          <Box className="Detail">
            <Box className="planDetail">
              <Box className="planned">
                <Typography variant="h3" className="plan">
                  Check in
                </Typography>
                {getAllCarers.map((carer, i) => {
                  const checkInTime = checkIns.find(
                    (el) => el.carer._id === carer._id
                  );

                  return (
                    <span
                      key={i}
                      className="timer"
                      style={{
                        display: "flex",
                        flexDirection: "column", // stack items vertically
                        alignItems: "flex-start", // left align
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <Box>
                          {checkInTime ? (
                            checkInTime?.forcedCheckIn ? (
                              <SVG.Warning width="20px" />
                            ) : (
                              <SVG.Check width="20px" />
                            )
                          ) : (
                            <SVG.Wrong width="20px" />
                          )}
                        </Box>

                        <Box>
                          {checkInTime
                            ? checkInTime?.manuallyDoneBy
                              ? `${checkInTime.manuallyDoneBy.name} manually checked in on behalf of ${carer?.name} ${carer?.type ? `(${carer?.type})` : ""} on ${dayjs(checkInTime.checkIn).utc(true).format("DD/MM/YYYY")} at ${dayjs(checkInTime.checkIn).utc().format("HH:mm")}`
                              : `${dayjs(checkInTime.checkIn).utc(true).format("DD/MM/YYYY")} at ${dayjs(checkInTime.checkIn).utc().format("HH:mm")} ${carer?.name} ${carer?.type ? `(${carer?.type})` : ""} checked in`
                            : `${carer?.name} ${carer?.type ? `(${carer?.type})` : ""} has not checked in`}{" "}
                          {locationMismatchEnabled &&
                            checkInTime?.forcedCheckIn && (
                              <Tooltip title="Click to view details" arrow>
                                <Chip
                                  icon={<InfoOutlined fontSize="small" />}
                                  label="Location mismatch"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  clickable
                                  sx={{
                                    fontSize: 10,
                                  }}
                                  onClick={() => {
                                    setIsLocationMismatchOpen(true);
                                    handleSelectLog(checkInTime, "checkIn");
                                  }}
                                />
                              </Tooltip>
                            )}
                          {checkInTime?.manuallyDoneBy && (
                            <Tooltip title="Click to view details" arrow>
                              <Chip
                                icon={<InfoOutlined fontSize="small" />}
                                label="Manual Check In"
                                size="small"
                                color="warning"
                                variant="outlined"
                                clickable
                                sx={{
                                  fontSize: 10,
                                }}
                                onClick={() => {
                                  setOpenManualType(TASK.CHECK_IN);
                                  handleSelectLog(checkInTime, "checkIn");
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </span>

                      {checkInTime?.checkInNote && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "10px",
                            color: "#505470",
                            mt: 1,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <strong>Note:</strong>{" "}
                          {displayText(checkInTime.checkInNote)}
                        </Typography>
                      )}
                    </span>
                  );
                })}
              </Box>
              <Box>
                <SVG.Login width="20px" />
              </Box>
            </Box>
          </Box>
          <Box className="Detail">
            <Box className="planDetail">
              <Box className="planned">
                <Typography variant="h3" className="plan">
                  Check Out
                </Typography>
                {getAllCarers.map((carer, i) => {
                  const checkOutTime = checkOuts.find(
                    (el) => el.carer._id === carer._id
                  );

                  const type = getCheckoutReasonType({
                    setting,
                    visit,
                    checkOutTime: dayjs(checkOutTime?.checkOut),
                    carerId: getId(carer),
                  });
                  const ui = getCheckoutUI(type);

                  return (
                    <span
                      key={i}
                      className="timer"
                      style={{
                        display: "flex",
                        flexDirection: "column", // stack content vertically
                        alignItems: "flex-start", // left align
                      }}
                    >
                      {/* Top row with icon + text */}
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <Box>
                          {checkOutTime ? (
                            checkOutTime?.auto ? (
                              <SVG.Wrong width="20px" />
                            ) : checkOutTime?.forcedCheckOut ? (
                              <SVG.Warning width="20px" />
                            ) : (
                              <SVG.Check width="20px" />
                            )
                          ) : (
                            <SVG.Wrong width="20px" />
                          )}
                        </Box>

                        <Box>
                          {checkOutTime
                            ? checkOutTime?.manuallyDoneBy
                              ? `${checkOutTime.manuallyDoneBy.name} manually checked out on behalf of ${carer?.name} ${carer?.type ? `(${carer?.type})` : ""} on ${dayjs(checkOutTime.checkOut).utc(true).format("DD/MM/YYYY")} at ${dayjs(checkOutTime.checkOut).utc().format("HH:mm")}`
                              : checkOutTime?.auto
                                ? `${carer?.name} ${carer?.type ? `(${carer?.type})` : ""} did not check out. (Force Check Out)`
                                : `${dayjs(checkOutTime.checkOut).utc(true).format("DD/MM/YYYY")} at ${dayjs(checkOutTime.checkOut).utc().format("HH:mm")} ${carer?.name} ${carer?.type ? `(${carer?.type})` : ""} checked out`
                            : `${carer?.name} ${carer?.type ? `(${carer?.type})` : ""} has not checked out`}{" "}
                          {locationMismatchEnabled &&
                            checkOutTime?.forcedCheckOut && (
                              <Tooltip title="Click to view details" arrow>
                                <Chip
                                  icon={<InfoOutlined fontSize="small" />}
                                  label="Location mismatch"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  clickable
                                  sx={{
                                    fontSize: 10,
                                  }}
                                  onClick={() => {
                                    setIsLocationMismatchOpen(true);
                                    handleSelectLog(checkOutTime, "checkOut");
                                  }}
                                />
                              </Tooltip>
                            )}
                          {(checkOutTime?.lateOrEarlyCheckoutReason ||
                            checkOutTime?.lateOrEarlyCheckoutReasonNote) && (
                            <Tooltip title="Click to view details" arrow>
                              <Chip
                                icon={ui.icon}
                                label={
                                  checkOutTime?.auto
                                    ? "Auto Checkout"
                                    : ui.label
                                }
                                color={ui.color}
                                size="small"
                                // color={ui.color}
                                variant="outlined"
                                clickable
                                onClick={() => {
                                  setIsCheckoutReasonDialog(true);
                                  handleSelectLog(checkOutTime, "checkOut");
                                }}
                                sx={{
                                  fontSize: 10,
                                  marginLeft: 1,
                                  marginRight: 1,
                                  borderColor: checkOutTime?.auto
                                    ? "#4682d5"
                                    : undefined,
                                  color: checkOutTime?.auto
                                    ? "#4682d5"
                                    : undefined,
                                }}
                              />
                            </Tooltip>
                          )}
                          {checkOutTime?.manuallyDoneBy && (
                            <Tooltip title="Click to view details" arrow>
                              <Chip
                                icon={<InfoOutlined fontSize="small" />}
                                label="Manual Check Out"
                                size="small"
                                color="warning"
                                variant="outlined"
                                clickable
                                sx={{
                                  fontSize: 10,
                                }}
                                onClick={() => {
                                  setOpenManualType(TASK.CHECK_OUT);
                                  handleSelectLog(checkOutTime, "checkOut");
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </span>

                      {checkOutTime?.checkOutNote && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "10px",
                            color: "#505470",
                            mt: 1,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <strong>Note:</strong>{" "}
                          {displayText(checkOutTime.checkOutNote)}
                        </Typography>
                      )}
                    </span>
                  );
                })}
              </Box>
              <Box>
                <SVG.Logout width="20px" />
              </Box>
            </Box>
          </Box>
          <Box className="Detail">
            <Box className="planDetail">
              <Box className="planned">
                <Typography variant="h3" className="plan">
                  Total visit time
                </Typography>
                {totalLogs.map((log, i) => {
                  return log?.auto ? (
                    <span key={i} className="timer">
                      Total hours not received: Needs Verification
                    </span>
                  ) : (
                    <span key={i} className="timer">
                      {displayText(log.carer.name, getId(log.carer))} total
                      visit time{" "}
                      {log.checkOut
                        ? getTotalTime(log.checkIn, log.checkOut)
                        : getTotalTime(log.checkIn, new Date())}
                    </span>
                  );
                })}
              </Box>
              <Box>
                <SVG.Clock width="20px" />
              </Box>
            </Box>
          </Box>
        </>
      ) : isUnAllocated || (totalLogs.length === 0 && !loading) ? (
        <> </>
      ) : (
        <DefaultLoader
          style={{
            height: "40vh",
          }}
        />
      )}

      <DialogueBox onClose={handleClose} open={open}>
        <VisitDetail onClose={handleClose} />
      </DialogueBox>
      <DialogueBox onClose={handleDeleteClose} open={deleteConfirm}>
        <DeleteVisit
          loading={deleteLoading}
          onClose={handleDeleteClose}
          handleConfirm={handleVisitDelete}
          visit={extendedProps}
        />
      </DialogueBox>
      <DialogueBox onClose={handleCancelClose} open={cancelConfirm}>
        <CancelVisit onClose={handleCancelClose} />
      </DialogueBox>
      <DialogueBox
        onClose={handleLocationMismatchClose}
        open={selectedLog !== null && isLocationMismatchOpen}
      >
        <LocationMismatch
          selectedLog={selectedLog}
          serviceUser={serviceUser}
          onClose={handleLocationMismatchClose}
        />
      </DialogueBox>

      <DialogueBox
        onClose={() => setOpenManualType(null)}
        open={selectedLog !== null && openManualType !== null}
      >
        {selectedLog ? (
          <ManualCheckInOutInfo
            selectedLog={selectedLog}
            type={openManualType}
            onClose={() => setOpenManualType(null)}
            fetchCheckInCheckOut={fetchCheckInCheckOut}
          />
        ) : (
          <></>
        )}
      </DialogueBox>
      <DialogueBox
        onClose={handleCheckoutReasonClose}
        open={
          setting !== null && selectedLog !== null && isCheckoutReasonDialog
        }
      >
        <CheckoutReason
          selectedLog={selectedLog}
          setting={setting}
          onClose={handleCheckoutReasonClose}
        />
      </DialogueBox>
    </DrawerStyled>
  );
}

export default DetailTab;
